import {useEffect, useState} from 'react'
import {BASE_URL} from '../../../api'
import config from '../../config'
import uploadFileSecurely from '../../helper/secureUpload'
import ImageCropModal from './ImageCropModal'
const {axios} = config

// Downscale big photos before upload (same as Push Notifications).
const shrinkImage = (file, maxW = 1600) =>
  new Promise((resolve) => {
    const img = new Image()
    img.onload = () => {
      const scale = Math.min(1, maxW / img.width)
      const c = document.createElement('canvas')
      c.width = Math.round(img.width * scale)
      c.height = Math.round(img.height * scale)
      c.getContext('2d').drawImage(img, 0, 0, c.width, c.height)
      c.toBlob(
        (b) => resolve(b ? new File([b], file.name.replace(/\.\w+$/, '') + '.jpg', {type: 'image/jpeg'}) : file),
        'image/jpeg',
        0.85
      )
    }
    img.onerror = () => resolve(file)
    img.src = URL.createObjectURL(file)
  })

// Home screen banner photo, stored on the Settings doc (2026-09-29; made
// reusable 2026-10-06 for the Online Sessions banner).
//   settingKey: field on GET /admin/settings (e.g. meditationsBannerUrl)
//   endpoint:   PUT endpoint that saves {url}
const HomeBannerCard = ({settingKey, endpoint, bannerName, title, description}) => {
  const [url, setUrl] = useState('')
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState('')
  // File or URL waiting in the "Position photo" tool
  const [cropSource, setCropSource] = useState(null)
  useEffect(() => {
    axios
      .get(`${BASE_URL}/admin/settings`)
      .then(({data}) => setUrl(data?.[settingKey] || ''))
      .catch(() => {})
  }, [settingKey])
  const save = async (next) => {
    const {data} = await axios.put(`${BASE_URL}${endpoint}`, {url: next})
    setUrl(data?.url || '')
  }
  const onFile = (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setNote('')
    setCropSource(file)
  }
  // Called by the Position photo tool with the framed JPEG.
  const onCropped = async (file) => {
    setBusy(true)
    setNote('')
    try {
      const {fileUrl} = await uploadFileSecurely(await shrinkImage(file, 1800))
      await save(fileUrl)
      setCropSource(null)
      setNote('Saved. The app shows it within a few minutes (after the app is reopened).')
    } catch (err) {
      setCropSource(null)
      setNote('Could not upload the image.')
    } finally {
      setBusy(false)
    }
  }
  const remove = async () => {
    if (!window.confirm('Go back to the built-in photo?')) return
    setBusy(true)
    try {
      await save('')
      setNote('Back to the built-in photo.')
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className='card mb-5'>
      <div className='card-body p-9'>
        <h3 className='fw-bolder mb-3'>{title || 'Home screen banner'}</h3>
        <p className='text-muted'>
          {description ||
            `The photo on the ${bannerName} banner on the app's Home screen.`}{' '}
          It shows as a thin, wide strip, so choose a landscape photo with the subject in the
          middle band. Leave it unset to use the built-in photo.
        </p>
        <div className='d-flex align-items-center gap-4 flex-wrap'>
          <div
            className='rounded'
            style={{
              width: 420,
              height: 95, // same shape as the phone banner (4.4:1)
              background: url ? `center / cover no-repeat url(${url})` : '#eee',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: url ? '#fff' : '#888',
              textShadow: url ? '0 1px 3px rgba(0,0,0,.6)' : 'none',
              letterSpacing: 1,
            }}
          >
            {url ? String(bannerName || '').toUpperCase() : 'Built-in photo'}
          </div>
          <label className='btn btn-sm btn-light-primary mb-0'>
            {busy ? 'Uploading…' : url ? 'Change photo' : 'Upload photo'}
            <input type='file' accept='image/*' hidden disabled={busy} onChange={onFile} />
          </label>
          {url && (
            <button className='btn btn-sm btn-light' disabled={busy} onClick={() => setCropSource(url)}>
              Reposition
            </button>
          )}
          {url && (
            <button className='btn btn-sm btn-light' disabled={busy} onClick={remove}>
              Use built-in photo
            </button>
          )}
        </div>
        {note && <div className='text-muted fs-7 mt-3'>{note}</div>}
        {cropSource && (
          <ImageCropModal
            source={cropSource}
            shape='banner'
            onCancel={() => setCropSource(null)}
            onDone={onCropped}
          />
        )}
      </div>
    </div>
  )
}

export default HomeBannerCard
